// Сборка провайдеров. PersistQueryClientProvider поднимает кэш прошлой сессии из
// localStorage до первого ответа сервера: правило 11, «0 мс — каркас и данные прошлой
// сессии с пометкой времени».

import { useEffect, type ReactNode } from 'react';

import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';

import { setForgetHandler } from '@/shared/auth/session';
import * as Toast from '@/shared/ui/toast';
import { назначитьПоказ } from '@/shared/ui/toast/подтверждение';

import {
  CACHE_BUSTER,
  CACHE_MAX_AGE_MS,
  persister,
  поднятоеНесвежее,
  queryClient,
} from './query-client';

// Уборка за ушедшим человеком. Регистрируется здесь, а не внутри слоя сессии: `shared/` не
// знает про клиент запросов, иначе получилось бы кольцо импортов. Та же связка, что у
// обработчика 401 (`setRefreshHandler`).
//
// Гасим и память, и диск. Одной памяти мало: `PersistQueryClientProvider` поднимает кэш из
// localStorage при следующей загрузке страницы, и чужие остатки вернулись бы на экран.
setForgetHandler(() => {
  queryClient.clear();
  void persister.removeClient();
});

/**
 * Кто показывает подтверждения удачных действий.
 *
 * Отдельным пустым компонентом внутри провайдера, а не хуком в `Providers`: контекст виден
 * только детям провайдера, и вызвать `useToast` в самом `Providers` нельзя — он стоит выше.
 */
function ПоказПодтверждений() {
  const { addToast } = Toast.useToast();
  useEffect(() => {
    назначитьПоказ(({ что, подробность }) => {
      addToast({ variant: 'success', title: что, description: подробность });
    });
    return () => назначитьПоказ(null);
  }, [addToast]);
  return null;
}

export function Providers({ children }: { children: ReactNode }) {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister, maxAge: CACHE_MAX_AGE_MS, buster: CACHE_BUSTER }}
      onSuccess={() => поднятоеНесвежее(queryClient)}
    >
      {/* Подтверждение действия — на весь продукт одно, и живёт оно ВЫШЕ каркаса.
          До 04.09.2026 после удачного действия человек не получал ничего за пределами открытой
          панели: закрыл её — и не знает, прошло ли. Панель при этом закрывается не всегда, а
          подтверждение нужно всегда.

          Провайдер стоит НАД маршрутизатором намеренно: подтверждение переживает переход между
          разделами. Человек утверждает ведомость и уходит в «Дела» — сообщение обязано доехать
          с ним, а не исчезнуть вместе с экраном, на котором он нажал. */}
      <Toast.Provider>
        <ПоказПодтверждений />
        {children}
        <Toast.Viewport />
      </Toast.Provider>
    </PersistQueryClientProvider>
  );
}
